// src/utils/auth.js
import {
  getTokenTtlMs,
  getSharedRemainingMs,
  resetSessionClock,
  clearSessionClock,
} from "./sessionClock";


const ACCESS_KEY = "access_token";
const TOKEN_TYPE_KEY = "token_type";
const EXPIRES_IN_KEY = "expires_in";
const USUARIO_KEY = "usuario";
const ACTOR_TYPE_KEY = "actorType";

const TOKEN_META_KEY = "flpa_token_meta";


export function decodeJwt(token) {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    try {
      return JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
    } catch {
      return null;
    }
  }
}

function buildTokenMeta(token) {
  const claims = decodeJwt(token);

  if (!claims?.iat || !claims?.exp) {
    return null;
  }

  const iat = Number(claims.iat);
  const exp = Number(claims.exp);

  if (!Number.isFinite(iat) || !Number.isFinite(exp)) {
    return null;
  }

  if (exp <= iat) {
    return null;
  }

  const ttlMs = (exp - iat) * 1000;

  if (ttlMs <= 30_000) {
    return null;
  }

  return {
    token,
    iat,
    exp,
    ttlMs,
  };
}

export function validateJwtToken(token) {
  return !!buildTokenMeta(token);
}

function saveTokenMeta(meta) {
  localStorage.setItem(TOKEN_META_KEY, JSON.stringify(meta));
}

function getTokenMeta() {
  try {
    const token = getAccessToken();
    if (!token) return null;

    const raw = localStorage.getItem(TOKEN_META_KEY);
    const meta = raw ? JSON.parse(raw) : null;

    if (meta?.token === token && meta?.ttlMs) {
      return meta;
    }

    const rebuilt = buildTokenMeta(token);
    if (!rebuilt) return null;

    saveTokenMeta(rebuilt);
    return rebuilt;
  } catch {
    return null;
  }
}

export function getTokenRemainingMs() {
  const remainingMs = getSharedRemainingMs();

  if (remainingMs == null) {
    return 0;
  }

  return remainingMs;
}

export function saveLogin(loginResponse) {
  if (!loginResponse?.access_token) return false;

  const meta = buildTokenMeta(loginResponse.access_token);
  if (!meta) {
    console.error("[auth] Login devolvió JWT inválido. No se guarda.");
    clearLogin();
    return false;
  }

  localStorage.setItem(ACCESS_KEY, loginResponse.access_token);
  saveTokenMeta(meta);

  resetSessionClock(
    getTokenTtlMs(loginResponse.access_token),
    null
  );

  if (loginResponse.token_type) {
    localStorage.setItem(TOKEN_TYPE_KEY, loginResponse.token_type);
  }

  if (loginResponse.expires_in != null) {
    localStorage.setItem(EXPIRES_IN_KEY, String(loginResponse.expires_in));
  }

  localStorage.removeItem("dashboardActiveView");
  return true;
}

export function setAccessToken(token) {
  if (!token) {
    clearLogin();
    return false;
  }

  const meta = buildTokenMeta(token);
  if (!meta) {
    console.error("[auth] Token renovado inválido. No se guarda.");
    return false;
  }

  saveTokenMeta(meta);
  localStorage.setItem(ACCESS_KEY, token);

  resetSessionClock(
    getTokenTtlMs(token),
    null
  );

  return true;
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY) || sessionStorage.getItem(ACCESS_KEY);
}

export function getUsuario() {
  return localStorage.getItem(USUARIO_KEY) || sessionStorage.getItem(USUARIO_KEY);
}

export function getActorType() {
  let type =
    localStorage.getItem(ACTOR_TYPE_KEY) ||
    sessionStorage.getItem(ACTOR_TYPE_KEY);

  if (type) return type;

  const claims = getClaims();
  return claims?.actorType || null;
}


export function getTokenRemainingSeconds() {
  return Math.floor(getTokenRemainingMs() / 1000);
}

export function isTokenExpired(bufferSec = 0) {
  return getTokenRemainingMs() <= bufferSec * 1000;
}

export function getClaims() {
  const t = getAccessToken();
  return decodeJwt(t) || {};
}

export function hasScope(scope) {
  const claims = getClaims();
  const sc = claims?.scope;

  if (!sc) return false;
  if (Array.isArray(sc)) return sc.includes(scope);
  if (typeof sc === "string") return sc.split(/\s+/).includes(scope);

  return false;
}

export function clearLogin() {
  clearSessionClock();
  const keys = [
    ACCESS_KEY,
    TOKEN_TYPE_KEY,
    EXPIRES_IN_KEY,
    TOKEN_META_KEY,
    "claims",
    "role",
    ACTOR_TYPE_KEY,
    "clientId",
    "scope",
    "user",
    "authUser",
    "usuario",
    "userId",
    "terminosCondiciones",
    "dashboardActiveView",
  ];

  keys.forEach((k) => {
    try {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    } catch {}
  });
}

export function logout() {
  clearLogin();

  const LOGIN_HASH = "#/";
  let here = window.location.hash || "#/";

  const cleanedHash = here
    .replace(/([?&])view=[^&]*&?/, "$1")
    .replace(/[?&]$/, "");

  if (cleanedHash !== here) {
    here = cleanedHash;
    window.location.hash = cleanedHash;
  }

  if (here !== LOGIN_HASH) {
    sessionStorage.setItem("returnTo", here);
  }

  if ((window.location.hash || "#/") !== LOGIN_HASH) {
    window.location.hash = LOGIN_HASH;
  }
}